import { HttpError } from '@/queries/auth-error'
import { isProdEnv } from '@/utils/isProdEnv'
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'

const integrations = [Sentry.browserTracingIntegration(), Sentry.replayIntegration()]

function isHttpErrorEvent(event: Sentry.Event, hint: Sentry.EventHint): boolean {
  if (hint.originalException instanceof HttpError) return true

  const exceptionValues = event.exception?.values ?? []
  return exceptionValues.some((exception) => exception.type === 'HttpError')
}

export default function sentryPlugin(app: App): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.log('Sentry is not enabled: VITE_SENTRY_DSN is not set.')
    return
  }

  const isProd = isProdEnv()

  Sentry.init({
    app,
    dsn,
    integrations,
    environment: isProd ? 'production' : 'development',
    tracePropagationTargets: [import.meta.env.VITE_API_BASE_URL],
    propagateTraceparent: true,
    tracesSampleRate: 0.1,
    // Session replay is the expensive one and dev sessions are not worth recording.
    // Error replays stay on everywhere because they are low volume and high signal.
    replaysSessionSampleRate: isProd ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Sentry holds JS exceptions. A non-2xx API response is not one, and the mutation
      // toast already handles it. The backend side of the call is in Honeycomb, reachable
      // from the trace ID this event carries.
      if (isHttpErrorEvent(event, hint)) return null
      return event
    },
  })
}
